import { useContext, useMemo } from 'react';
import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Material,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Points,
  Scene,
  ShaderMaterial,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { Text } from 'troika-three-text';

import { GcpModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import roundPointsVColorFragmentShader from '../../../../../static/glsl/points/round-points-vcolor.frag';
import vColorVertShader from '../../../../../static/glsl/points/vcolor.vert';
import { isDefined } from '../../../../utils/variables';
import { sfmViewerColors } from '../../../../viewer/elements/design/colors';
import { renderOrders } from '../../../../viewer/render-orders';
import { configureTexts } from '../../../../viewer/utilities/three/text-builder';
import { GeodataContext } from '../geodata-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';

type Gcp = { points: Points; labels: Object3D };

export function useGcpPoints() {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const { geodata } = useContext(GeodataContext);

  const gcp = useMemo<Gcp | undefined>(() => {
    if (
      !isDefined(origo) ||
      !isDefined(geodata?.gcpCollection?.points) ||
      geodata.gcpCollection.points.length === 0
    ) {
      return undefined;
    }

    configureTexts();

    const points = createGcpPointsGeometry(geodata.gcpCollection.points, origo);
    const labels = createGcpLabelsTroika(geodata.gcpCollection.points, origo);

    return { points, labels };
  }, [geodata, origo]);

  return { gcp };
}

function getGcpPointColorRGB(gcp: GcpModel): [number, number, number] {
  if (gcp.status === 'ENABLED') {
    return sfmViewerColors.gcp.default;
  }
  if (gcp.status === 'DISABLED') {
    return sfmViewerColors.gcp.disabled;
  }
  return sfmViewerColors.gcp.checkpoint;
}

function createGcpPointsGeometry(gcpPoints: GcpModel[], origo: Vector3) {
  const position = gcpPoints.map((x) => new Vector3(...x.enu).sub(origo).toArray()).flat();

  const colors = new Uint8Array(gcpPoints.length * 3);
  for (let i = 0; i < gcpPoints.length; i++) {
    const rgb = getGcpPointColorRGB(gcpPoints[i]);
    colors[i * 3] = Math.round(rgb[0] * 255);
    colors[i * 3 + 1] = Math.round(rgb[1] * 255);
    colors[i * 3 + 2] = Math.round(rgb[2] * 255);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
  geometry.setAttribute('color', new BufferAttribute(colors, 3, true));
  geometry.computeBoundingSphere();

  const points = new Points(
    geometry,
    new ShaderMaterial({
      vertexShader: vColorVertShader,
      fragmentShader: roundPointsVColorFragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true,
      uniforms: {
        size: { value: 10 },
      },
    }),
  );
  points.renderOrder = renderOrders.aligner.gcpPoints;
  return points;
}

function createGcpLabelsTroika(gcpPoints: GcpModel[], origo: Vector3) {
  const labelsRoot = new Object3D();
  const textMaterial = new MeshBasicMaterial({
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
    side: DoubleSide,
  });

  for (const gcp of gcpPoints) {
    labelsRoot.add(createGcpTroikaLabel(gcp, origo, textMaterial));
  }
  return labelsRoot;
}

function getWorldScreenH(camera: Camera, text: Text, TMP_A: Vector3, TMP_B: Vector3) {
  if ((camera as PerspectiveCamera).isPerspectiveCamera) {
    const cam = camera as PerspectiveCamera;
    const vFov = (cam.fov * Math.PI) / 180;

    text.getWorldPosition(TMP_A);
    cam.getWorldPosition(TMP_B);
    const dist = TMP_A.distanceTo(TMP_B);

    return 2 * dist * Math.tan(vFov / 2);
  } else {
    const cam = camera as OrthographicCamera;
    return (cam.top - cam.bottom) / (cam.zoom || 1);
  }
}

function createGcpTroikaLabel(gcp: GcpModel, origo: Vector3, textMaterial: Material) {
  const pos = new Vector3(...gcp.enu).sub(origo);

  const text = new Text();

  text.text = gcp.name;
  text.fontSize = 5;
  text.color = 0xffffff;
  text.anchorX = -2.5;
  text.anchorY = 'bottom-baseline';
  text.fontWeight = 'bold';
  text.position.copy(pos);
  text.renderOrder = renderOrders.aligner.gcpLabels;
  text.material = textMaterial;

  text.outlineColor = 0x000000;
  text.outlineOpacity = 0.8;
  text.outlineWidth = 1.5;

  // Store the original method bound to the text instance
  const troikaOnBeforeRender = text.onBeforeRender.bind(text);

  const TMP_V2 = new Vector2();
  const TMP_A = new Vector3();
  const TMP_B = new Vector3();
  const TARGET_PX = 14;

  text.onBeforeRender = function (
    this: Text,
    renderer: WebGLRenderer,
    scene: Scene,
    camera: Camera,
    geometry: BufferGeometry,
    material: Material,
    group: Group,
  ) {
    troikaOnBeforeRender.call(this, renderer, scene, camera, geometry, material, group);

    // Billboard. (Text faces camera)
    text.quaternion.copy(camera.quaternion);

    // Size Attenuation
    renderer.getSize(TMP_V2);
    const viewportH = TMP_V2.y;
    const worldScreenH = getWorldScreenH(camera, text, TMP_A, TMP_B);
    const worldPerPixel = worldScreenH / viewportH;
    const scale = (TARGET_PX * worldPerPixel) / text.fontSize;
    text.scale.setScalar(scale);
  };

  void text.sync();

  return text;
}
